export const LINKEDIN_PROFILE_PREFIX = "https://linkedin.com/in/";

const LINKEDIN_ERROR = "Enter a LinkedIn profile username or /in/ URL.";
const E164_MAX_DIGITS = 15;
const PHONE_ERROR = "Enter a complete phone number with a valid country code.";

/**
 * The conservative text contract used immediately before persistence.
 *
 * All whitespace runs become one ordinary space. This removes accidental tabs,
 * newlines, and repeated spaces without changing capitalization or punctuation.
 */
export function normalizeSaveText(value: string): string {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

/** Canonical email storage and lookup form. Shape validation remains with Zod. */
export function normalizeEmailAddress(value: string): string {
  return normalizeSaveText(value).toLowerCase();
}

/** Omitted means preserve, while an explicit blank means clear. */
export function normalizeOptionalSaveText(
  value: string | null | undefined,
): string | null | undefined {
  if (value === undefined) return undefined;
  const normalized = normalizeSaveText(value ?? "");
  return normalized || null;
}

/**
 * Normalizes one optional phone for storage.
 *
 * A blank clears the field. A nonblank value must be unambiguous enough for the
 * existing E.164 parser; retaining malformed user input would violate the save
 * contract and make equivalent values diverge again.
 */
export function normalizePhoneForSave(
  value: string | null | undefined,
): string | null | undefined {
  if (value === undefined) return undefined;
  if (!normalizeSaveText(value ?? "")) return null;
  const normalized = toE164Phone(value);
  if (!normalized) throw new Error(PHONE_ERROR);
  return normalized;
}

/** Drops blanks and case-insensitive duplicates while preserving first-seen case and order. */
export function normalizeStringList(
  input: readonly (string | null | undefined)[] = [],
): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const value of input) {
    const normalized = normalizeSaveText(value ?? "");
    if (!normalized) continue;
    const key = normalized.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    values.push(normalized);
  }
  return values;
}

function linkedinUsername(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (!trimmed.includes("://") && !trimmed.includes("/")) return trimmed;

  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
    const match = url.pathname.match(/^\/in\/([^/]+)\/?$/i);
    if (url.protocol !== "https:" || hostname !== "linkedin.com" || !match) throw new Error(LINKEDIN_ERROR);
    return decodeURIComponent(match[1]);
  } catch {
    throw new Error(LINKEDIN_ERROR);
  }
}

export function normalizeLinkedInProfile(value: string) {
  const username = linkedinUsername(value);
  if (!username) return undefined;
  if (!/^[a-zA-Z0-9-]{2,100}$/.test(username)) throw new Error(LINKEDIN_ERROR);
  return `${LINKEDIN_PROFILE_PREFIX}${username}`;
}

/** Omitted means preserve, blank means clear, and a value is canonicalized. */
export function normalizeOptionalLinkedInProfile(
  value: string | null | undefined,
): string | null | undefined {
  if (value === undefined) return undefined;
  return normalizeLinkedInProfile(value ?? "") ?? null;
}

function digitsOnly(value: string) {
  return value.replace(/\D/g, "");
}

/**
 * The flag emoji a country picker may carry, removed before anything is parsed.
 *
 * The picker shows "🇨🇦 +1" because a flag identifies a country faster than a
 * name does, and a label like that reaches these functions whenever a caller
 * passes what the control displayed instead of the dial code behind it. The
 * emoji is not a digit, so it survives to the length checks and turns a valid
 * number into "Choose a valid country code".
 *
 * Stripped here rather than at each call site: this is the one place both the
 * country code and the number are parsed, so a caller cannot forget.
 *
 * Regional-indicator pairs (U+1F1E6–U+1F1FF) and variation selectors, matched by
 * code point so the build target's regex support is not relied on.
 */
function stripDecorations(value: string) {
  let cleaned = "";
  for (const character of String(value ?? "")) {
    const point = character.codePointAt(0) ?? 0;
    const isRegionalIndicator = point >= 0x1f1e6 && point <= 0x1f1ff;
    const isVariationSelector = point >= 0xfe00 && point <= 0xfe0f;
    const isZeroWidthJoiner = point === 0x200d;
    if (isRegionalIndicator || isVariationSelector || isZeroWidthJoiner) continue;
    cleaned += character;
  }
  return cleaned.trim();
}

function countryDigits(countryCode: string) {
  const digits = digitsOnly(stripDecorations(countryCode));
  if (!digits || digits.length > 3) throw new Error("Choose a valid country code.");
  return digits;
}

export function formatNationalPhone(countryCode: string, value: string) {
  const digits = digitsOnly(stripDecorations(value));
  if (countryDigits(countryCode) === "1") {
    const national = digits.startsWith("1") && digits.length > 10 ? digits.slice(1, 11) : digits.slice(0, 10);
    if (!national) return "";
    if (national.length < 4) return `(${national}`;
    if (national.length < 7) return `(${national.slice(0, 3)}) ${national.slice(3)}`;
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }

  return digits
    .slice(0, Math.max(0, E164_MAX_DIGITS - countryDigits(countryCode).length))
    .replace(/(\d{3})(?=\d)/g, "$1 ")
    .trim();
}

export function normalizeInternationalPhone(countryCode: string, value: string) {
  const nationalDigits = digitsOnly(stripDecorations(value));
  if (!nationalDigits) return undefined;

  const prefixDigits = countryDigits(countryCode);
  const normalizedNational =
    prefixDigits === "1" && nationalDigits.length === 11 && nationalDigits.startsWith("1")
      ? nationalDigits.slice(1)
      : nationalDigits;
  const totalDigits = `${prefixDigits}${normalizedNational}`;

  if (normalizedNational.length < 7) throw new Error("Enter a complete phone number.");
  if (totalDigits.length > E164_MAX_DIGITS) throw new Error("Enter a valid international phone number.");
  return `+${totalDigits}`;
}

/**
 * Coerces any stored phone value into E.164 (`+19723590607`).
 *
 * Storage has to be E.164 because Stripe and every other integration expects it, but
 * historical rows are not: signup once stored `+1 (972) 359-0607`, other paths stored
 * bare digits, and admin-entered numbers were whatever was typed. This accepts all of
 * those and returns one shape.
 *
 * A bare ten-digit number is assumed to be +1. That assumption is only safe because
 * this is a US-based organisation whose signup form defaults to +1 — it is not a
 * general-purpose rule, and a number already carrying a country code is never
 * re-prefixed.
 *
 * Returns null rather than a guess when the input cannot be a phone number. Writing a
 * malformed value back would be worse than leaving the original alone, because the
 * original is at least still legible to a human.
 */
export function toE164Phone(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;

  const digits = digitsOnly(raw);
  if (!digits) return null;

  // An explicit "+" means the country code is already present and must be preserved.
  if (raw.startsWith("+")) {
    if (digits.length < 8 || digits.length > E164_MAX_DIGITS) return null;
    return `+${digits}`;
  }

  // 11 digits starting with 1 is a US number written with its country code.
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;

  // Anything else is ambiguous: too short to be complete, or long enough to carry an
  // unknown country code that must not be assumed.
  return null;
}

/**
 * Renders a stored number for people: `(972) 359-0607`.
 *
 * Only US numbers get the parenthesised form, because that pattern means nothing
 * elsewhere and would misgroup the digits of, say, a UK number. Anything else is shown
 * in its E.164 form, which is unambiguous even if less pretty.
 *
 * Falls back to the original string when it cannot be parsed, so an unrecognised
 * legacy value stays visible to an administrator instead of vanishing — a blank cell
 * reads as "no phone on file", which is a different and misleading fact.
 */
export function formatPhoneForDisplay(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;

  const e164 = toE164Phone(raw);
  if (!e164) return raw;

  const digits = e164.slice(1);
  if (digits.length === 11 && digits.startsWith("1")) {
    const national = digits.slice(1);
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }

  return e164;
}
